<template>
  <div class="parking-management-bg">
    <div v-if="!isSubmissionSuccess" class="form-content">
      <h2 class="page-title">出售車位</h2>
      <p class="title-description">勾選有興趣購買車位的場地並留下聯絡方式，我們將儘快與您聯繫</p>

      <!-- 第一部分：出售車位場地 -->
      <div class="form-card">
        <h4 class="sub-title">出售車位場地（可複選）</h4>

        <p v-if="isLoadingLots" class="sale-hint">場地資料載入中…</p>
        <p v-else-if="loadError" class="sale-hint">{{ loadError }}</p>
        <p v-else-if="lots.length === 0" class="sale-hint">目前暫無出售車位的場地，敬請期待。</p>

        <div v-else class="sale-lots">
          <button
            v-for="lot in lots"
            :key="lot.id"
            type="button"
            :class="['sale-lot', { active: selectedIds.includes(lot.id) }]"
            :aria-pressed="selectedIds.includes(lot.id) ? 'true' : 'false'"
            @click="toggleLot(lot.id)"
          >
            <span class="sale-lot-check" aria-hidden="true">{{ selectedIds.includes(lot.id) ? '✓' : '' }}</span>
            <span class="sale-lot-text">
              <span class="sale-lot-title">{{ lot.title }}</span>
              <span class="sale-lot-address">{{ lot.address }}</span>
              <span v-if="lotTypes(lot).length || lotHeight(lot)" class="sale-lot-types">
                <span v-for="t in lotTypes(lot)" :key="t" class="sale-lot-type">{{ t }}</span>
                <span v-if="lotHeight(lot)" class="sale-lot-type sale-lot-height">{{ lotHeight(lot) }}</span>
              </span>
            </span>
          </button>
        </div>
      </div>

      <template v-if="lots.length > 0">
        <!-- 電洽資訊 -->
        <div class="sale-call">
          <span class="sale-call-lead">立即購買</span>
          <span class="sale-call-text">歡迎電洽</span>
          <a class="sale-call-phone" href="tel:0925751626">0925-751-626</a>
          <span class="sale-call-text">李小姐</span>
        </div>

        <!-- 第二部分：聯絡資料 -->
        <div class="form-card">
          <h4 class="sub-title">聯絡資料</h4>
          <div class="two-columns">
            <div class="form-group">
              <label for="saleName">姓名</label>
              <input
                type="text"
                id="saleName"
                v-model.trim="formData.customer_name"
                maxlength="50"
                placeholder="請輸入您的姓名"
                class="font-control"
              />
            </div>
            <div class="form-group">
              <label for="salePhone">聯絡電話</label>
              <input
                type="tel"
                id="salePhone"
                v-model.trim="formData.contact_phone"
                maxlength="20"
                placeholder="請輸入您的聯絡電話"
                class="font-control"
              />
            </div>
          </div>
        </div>

        <!-- 驗證碼（4 碼數字） -->
        <div class="form-card">
          <div class="form-group">
            <label for="saleCaptcha">驗證碼</label>
            <div class="two-columns">
              <input
                type="text"
                id="saleCaptcha"
                v-model.trim="verificationCode"
                inputmode="numeric"
                pattern="[0-9]*"
                autocomplete="off"
                maxlength="4"
                placeholder="請輸入 4 碼數字"
                class="font-control"
              />
              <CaptchaComponent
                ref="captcha"
                :length="4"
                :digits-only="true"
                @update-captcha-code="updateCaptchaCode"
              />
            </div>
          </div>
        </div>

        <button type="button" class="form-submit-btn" :disabled="isSubmitting" @click="submitForm">
          {{ isSubmitting ? '送出中…' : '確認無誤送出' }}
        </button>
      </template>
    </div>

    <SuccessScreen
      v-if="isSubmissionSuccess"
      message="您的出售車位登記已經送出<br />我們將儘快以電話與您聯繫"
    />
  </div>
</template>

<script>
import CaptchaComponent from '@/components/CaptchaComponent';
import PullDAO from '@/services/PullDAO';
import SuccessScreen from './SuccessScreen.vue';

const PHONE_RE = /^[0-9+\-() #]{8,20}$/;

// 與後台「場站型態／提供車位」同名（小型車位幾乎每場都有，不顯示）
const TYPE_LABELS = [
  ['is_street', '平面停車場'],
  ['is_garage', '立體停車場'],
  ['is_underground', '地下停車場'],
  ['is_tower', '機械車塔'],
  ['is_car_large', '大型車位'],
  ['is_electric', '充電車位'],
  ['is_moto', '普通機車'],
  ['is_moto_heavy', '重型機車'],
];

export default {
  name: 'ParkingSaleForm',
  components: { CaptchaComponent, SuccessScreen },
  data() {
    return {
      lots: [],
      selectedIds: [],
      isLoadingLots: true,
      loadError: '',
      formData: {
        customer_name: '',
        contact_phone: '',
      },
      verificationCode: '',
      captchaCode: '',
      isSubmitting: false,
      isSubmissionSuccess: false,
    };
  },
  async mounted() {
    try {
      const res = await new PullDAO().listSaleParkings();
      if (res && res.success) {
        this.lots = res.parkings || [];
      } else {
        this.loadError = '場地資料載入失敗，請稍後再試。';
      }
    } catch (e) {
      this.loadError = '場地資料載入失敗，請稍後再試。';
    } finally {
      this.isLoadingLots = false;
    }
  },
  methods: {
    lotTypes(lot) {
      return TYPE_LABELS.filter(([k]) => Number(lot[k]) === 1).map(([, v]) => v);
    },
    // 限高：後台有填「出售車位限高說明」就顯示說明；否則顯示「限高 1.9M」，未設定或 0 不顯示
    lotHeight(lot) {
      const note = String(lot.max_height_note || '').trim();
      if (note) return `限高 ${note}`;
      const h = Number(lot.max_height);
      return h > 0 ? `限高 ${h}M` : '';
    },
    toggleLot(id) {
      const i = this.selectedIds.indexOf(id);
      if (i === -1) this.selectedIds.push(id);
      else this.selectedIds.splice(i, 1);
    },
    updateCaptchaCode(code) {
      this.captchaCode = code;
    },
    refreshCaptcha() {
      this.verificationCode = '';
      if (this.$refs.captcha) this.$refs.captcha.generateCaptcha();
    },
    async submitForm() {
      if (this.isSubmitting) return;

      if (this.selectedIds.length === 0) {
        alert('請至少勾選一個場地。');
        return;
      }
      if (!this.formData.customer_name) {
        alert('請輸入姓名。');
        return;
      }
      if (!PHONE_RE.test(this.formData.contact_phone)) {
        alert('請輸入正確的聯絡電話。');
        return;
      }
      if (this.verificationCode !== this.captchaCode) {
        alert('驗證碼錯誤，請重新輸入。');
        this.refreshCaptcha();
        return;
      }

      this.isSubmitting = true;
      try {
        const res = await new PullDAO().addParkingSale({
          parking_ids: this.selectedIds,
          customer_name: this.formData.customer_name,
          contact_phone: this.formData.contact_phone,
        });
        if (res && res.success) {
          this.isSubmissionSuccess = true;
        } else {
          alert((res && res.message) || '提交失敗，請稍後再試！');
          this.refreshCaptcha();
        }
      } catch (e) {
        alert('提交失敗，請稍後再試！');
        this.refreshCaptcha();
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
@import "@/css/form.css";

.page-title {
  margin-bottom: 0px;
}

.sale-hint {
  margin: 8px 0;
  font-size: 18px;
  color: #555;
  text-align: left;
}

.sale-lots {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 24px;
}

.sale-lot {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border: 1.5px solid var(--gray11, #ccc);
  border-radius: 10px;
  background-color: #fff;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: #000;
}

.sale-lot.active {
  border: 2px solid #008000;
}

.sale-lot:focus-visible {
  outline: 3px solid #fff100;
  outline-offset: 2px;
}

.sale-lot-check {
  flex: 0 0 26px;
  height: 26px;
  border: 1.5px solid #999;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #fff;
}

.sale-lot.active .sale-lot-check {
  background-color: #008000;
  border-color: #008000;
}

.sale-lot-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.sale-lot-title {
  font-size: 20px;
  font-weight: 700;
}

.sale-lot-address {
  font-size: 15px;
  color: #666;
}

.sale-call {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 6px 12px;
  margin: 0 0 20px;
  padding: 18px 24px;
  border-radius: 12px;
  background-color: #fff8d6;
  border: 2px solid #f0c400;
  color: #333;
  font-size: 22px;
  font-weight: 700;
}

.sale-call-lead {
  color: #008000;
  font-size: 24px;
}

.sale-call-phone {
  color: #d32f2f;
  font-size: 26px;
  text-decoration: none;
  letter-spacing: 0.02em;
}

.sale-call-phone:hover {
  text-decoration: underline;
}

.sale-lot-types {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.sale-lot-type {
  padding: 2px 10px;
  border-radius: 999px;
  background-color: #e8f3ea;
  color: #1f6b35;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.6;
}

.sale-lot-height {
  background-color: #fff3e0;
  color: #b45309;
}

.form-submit-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

@media screen and (max-width: 560px) {
  .sale-lots {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .sale-lot-title {
    font-size: 17px;
  }

  .sale-lot-address {
    font-size: 13px;
  }

  .sale-lot-type {
    font-size: 13px;
  }

  .sale-call {
    font-size: 17px;
    padding: 14px 12px;
  }

  .sale-call-lead {
    font-size: 18px;
  }

  .sale-call-phone {
    font-size: 20px;
  }
}
</style>
